import React from "react";

/**
 * Settings -> Couriers: the courier's tracking API and its credentials, for
 * Tracking Info's "Get tracking" button. Shared by Add and Update; fields are
 * registered on the form's react-hook-form.
 *
 * The secrets are WRITE-ONLY here: the API stores them encrypted and never
 * sends them back -- only `trackingKeyLast4` and `trackingSecretSet` (nova-api
 * scripts/verify-courier-tracking-credentials.js). Blank keeps what is saved.
 */

export const TRACKING_PROVIDERS = [
  { value: "", label: "None" },
  { value: "dhl", label: "DHL" },
  { value: "ups", label: "UPS" },
];

// What each API calls its two credentials, and where to get them.
const LABELS = {
  dhl: { key: "API Key", secret: "API Secret", where: "From the MyDHL API (DHL Express) developer portal." },
  ups: {
    key: "Client ID",
    secret: "Client Secret",
    where: "From your app on developer.ups.com -- the app must include the Tracking API product.",
  },
};

export const trackingSummary = (courier) => {
  if (!courier?.trackingProvider) return "—";
  const name = courier.trackingProvider.toUpperCase();
  return courier.trackingKeyLast4 ? `${name} · ••••${courier.trackingKeyLast4}` : `${name} · no keys`;
};

export default function TrackingApiFields({ register, watch, saved = null }) {
  const provider = watch("trackingProvider") ?? saved?.trackingProvider ?? "";
  const labels = LABELS[provider];
  // Saved credentials belong to the saved API; picking another drops them.
  const showSaved = saved?.trackingKeyLast4 && provider === saved?.trackingProvider;

  return (
    <div className="mb-3 border rounded p-2">
      <label htmlFor="trackingProvider" className="form-label mb-1">
        Tracking API
      </label>
      <select
        id="trackingProvider"
        className="form-select"
        defaultValue={saved?.trackingProvider || ""}
        {...register("trackingProvider")}
      >
        {TRACKING_PROVIDERS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
      <small className="text-muted d-block">
        Used by the "Get tracking" button in Shipments.
      </small>

      {labels && (
        <>
          {showSaved && (
            <div className="small mt-2" data-testid="saved-credentials">
              Saved: {labels.key} ••••{saved.trackingKeyLast4}
              {saved.trackingSecretSet ? `, ${labels.secret} saved` : ""}. Leave the boxes blank to keep them.
            </div>
          )}
          <div className="row mt-2">
            <div className="col-md-6 mb-2">
              <label htmlFor="trackingKey" className="form-label mb-0 small">
                {labels.key}
              </label>
              <input
                id="trackingKey"
                className="form-control"
                autoComplete="off"
                placeholder={showSaved ? "Leave blank to keep" : ""}
                {...register("trackingKey")}
              />
            </div>
            <div className="col-md-6 mb-2">
              <label htmlFor="trackingSecret" className="form-label mb-0 small">
                {labels.secret}
              </label>
              <input
                id="trackingSecret"
                type="password"
                className="form-control"
                autoComplete="new-password"
                placeholder={showSaved ? "Leave blank to keep" : ""}
                {...register("trackingSecret")}
              />
            </div>
          </div>
          <small className="text-muted d-block">{labels.where}</small>
          {showSaved && (
            <div className="form-check mt-1">
              <input
                id="clearTrackingCredentials"
                type="checkbox"
                className="form-check-input"
                {...register("clearTrackingCredentials")}
              />
              <label htmlFor="clearTrackingCredentials" className="form-check-label small">
                Remove the saved credentials
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
}
